/**
 * Видео-референсы: клиент `/api/video-refs/*` (спека §11) — ключи react-query и хуки.
 *
 * Правки сцен и заметки отдают свежий `detail` — он кладётся в кэш карточки, перезапрос не нужен.
 * ДЕНЬГИ: `describe` и `project` (композер) платные. Ключ идемпотентности живёт до УСПЕШНОГО ответа
 * на тот же набор параметров (как `useGenerate`): двойной клик и повтор после обрыва уходят с тем же
 * ключом, сервер не платит второй раз; другой набор параметров — новый ключ.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiFailure, apiFetch, query } from "@/lib/api";
import { errorText, newKey, postJson } from "@/lib/project";
import { classify, SaveQueue } from "@/lib/saveQueue";
import {
  inProgress,
  noteOps,
  sendNotes,
  settleNotes,
  type CreatedRef,
  type DescribeResult,
  type NoteEdits,
  shouldKeepKey,
  type ProjectResult,
  type VideoRefDetail,
  type VideoRefList,
} from "@/lib/videoRef";

export type VideoRefListQuery = { status?: string; platform?: string };

export const videoRefKeys = {
  list: (q: VideoRefListQuery) => ["video-refs", q] as const,
  one: (id: number) => ["video-ref", id] as const,
};

const base = "/api/video-refs";
const POLL_MS = 3000;

// ---------------------------------------------------------------------------
// чтение
// ---------------------------------------------------------------------------

/** Список; пока есть референсы в работе — опрос раз в 3 с. */
export function useVideoRefs(q: VideoRefListQuery) {
  return useQuery({
    queryKey: videoRefKeys.list(q),
    queryFn: () => apiFetch<VideoRefList>(base + query({ status: q.status, platform: q.platform })),
    refetchInterval: (state) => (state.state.data?.items.some((r) => inProgress(r.status)) ? POLL_MS : false),
  });
}

/** Карточка; `queued|fetching|splitting` — опрос раз в 3 с. */
export function useVideoRef(id: number) {
  return useQuery({
    queryKey: videoRefKeys.one(id),
    queryFn: () => apiFetch<VideoRefDetail>(`${base}/${id}`),
    enabled: Number.isFinite(id),
    refetchInterval: (state) => (inProgress(state.state.data?.ref.status) ? POLL_MS : false),
  });
}

// ---------------------------------------------------------------------------
// запись
// ---------------------------------------------------------------------------

function put(client: QueryClient, detail: VideoRefDetail) {
  client.setQueryData(videoRefKeys.one(detail.ref.id), detail);
  void client.invalidateQueries({ queryKey: ["video-refs"] });
}

function seed(client: QueryClient, created: CreatedRef) {
  put(client, created.ref);
}

/** Новый референс из ссылки или видео-пина (ровно одно из `url`/`pin_id`). */
export function useCreateVideoRef() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { url?: string; pin_id?: number; note?: string }) => postJson<CreatedRef>(base, body),
    onSuccess: (got) => seed(client, got),
  });
}

/** Референс из файла (multipart `file` + `note`). */
export function useUploadVideoRef() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, note }: { file: File; note?: string }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("note", note ?? "");
      return apiFetch<CreatedRef>(`${base}/upload`, { method: "POST", body: form });
    },
    onSuccess: (got) => seed(client, got),
  });
}

function patchRefNote(id: number, note: string | null) {
  return apiFetch<VideoRefDetail>(`${base}/${id}`, { method: "PATCH", body: JSON.stringify({ note }) });
}

function patchScene(id: number, sceneId: number, body: ScenePatch) {
  return apiFetch<VideoRefDetail>(`${base}/${id}/scenes/${sceneId}`, { method: "PATCH", body: JSON.stringify(body) });
}

/** Общий комментарий к референсу. */
export function useSetRefNote(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (note: string | null) => patchRefNote(id, note),
    onSuccess: (got) => put(client, got),
  });
}

/** Отказ правки человеку: 409 `NOT_READY`/`BUSY` — подписи экрана (§12), остальное — текст сервиса. */
export function refErrorText(error: unknown): string {
  if (!error) return "";
  if (error instanceof ApiFailure && error.code === "NOT_READY") return "референс ещё разбирается — правки после разбора";
  if (error instanceof ApiFailure && error.code === "BUSY") return "описание уже идёт";
  return errorText(error);
}

const NOTE_DELAY_MS = 1000;
const noop = () => {};

/**
 * Комментарии карточки (общий и сцен) — автосохранение через 1 с очередью `SaveQueue` (как `/posts/:id`):
 * один запрос в полёте, правки во время запроса уходят следующим. Поле показывает свою правку поверх
 * сервера, пока она не сохранена. `drain` — перед платным вызовом: комментарии уходят композеру.
 * Экран монтирует хук заново на каждый референс (`key`).
 */
export function useNoteAutosave(id: number) {
  const client = useQueryClient();
  const [local, setLocalState] = useState<NoteEdits>({});
  const localRef = useRef<NoteEdits>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const setLocal = (e: NoteEdits) => {
    localRef.current = e;
    setLocalState(e);
  };

  const queueRef = useRef<SaveQueue<VideoRefDetail, NoteEdits> | null>(null);
  if (!queueRef.current) {
    queueRef.current = new SaveQueue<VideoRefDetail, NoteEdits>(
      {
        // Общий комментарий и сцены — разные ручки; каждая цель сама по себе (`sendNotes`): удачная сразу в
        // кэше и снята с поля, отказ 4xx не роняет соседей.
        patch: (body) =>
          sendNotes<VideoRefDetail>(
            body,
            (t) => (t.kind === "ref" ? patchRefNote(id, t.note) : patchScene(id, t.sceneId, { note: t.note })),
            (e) => classify(e) === "reject",
            (one, got) => {
              if (got) put(client, got);
              setLocal(settleNotes(localRef.current, one));
            },
          ),
        onSaved: (got, sent) => {
          put(client, got);
          setLocal(settleNotes(localRef.current, sent));
          setError(null);
        },
        onFailed: (e, kind, sent) => {
          setError(e);
          // 4xx (не готов, сцена удалена, длинно) — сервер это не примет: поле возвращается к серверу
          if (kind !== "retry") setLocal(settleNotes(localRef.current, sent));
          if (kind === "stale") queueRef.current?.reset();
        },
        onSaving: setSaving,
      },
      noteOps,
    );
  }
  const queue = queueRef.current;

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const edit = (key: string, text: string) => {
    setLocal({ ...localRef.current, [key]: text });
    queue.add({ [key]: text });
    clearTimer();
    timer.current = setTimeout(() => void queue.flush().catch(noop), NOTE_DELAY_MS);
  };

  // уход со страницы: накопленное — отправить; закрытие вкладки с несохранённым — спросить
  useEffect(() => {
    const warn = (ev: BeforeUnloadEvent) => {
      if (queue.pending) ev.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      clearTimer();
      void queue.flush().catch(noop);
    };
  }, [queue]);

  return {
    /** Текст поля: своя несохранённая правка, иначе сервер. */
    value: (key: string, server: string | null) => (key in local ? (local[key] as string) : (server ?? "")),
    edit,
    saving,
    error,
    /** Дождаться сохранения всего (перед платным вызовом). Отказ — наружу. */
    drain: () => {
      clearTimer();
      return queue.drain();
    },
  };
}

export function useDeleteVideoRef() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch<{ deleted: number; projects: number }>(`${base}/${id}`, { method: "DELETE" }),
    onSuccess: (_r, id) => {
      client.removeQueries({ queryKey: videoRefKeys.one(id) });
      void client.invalidateQueries({ queryKey: ["video-refs"] });
      void client.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/** Переразбор (бесплатно): число — порог, строка — пресет. Правки и отметки теряются. */
export function useReanalyze(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (threshold?: number | "more" | "normal" | "fewer") => postJson<VideoRefDetail>(`${base}/${id}/analyze`, { threshold }),
    onSuccess: (got) => put(client, got),
  });
}

// ---------------------------------------------------------------------------
// сцены
// ---------------------------------------------------------------------------

export type ScenePatch = { mark?: "use" | "skip"; note?: string | null; key_ms?: number };

/** Все правки сцен возвращают свежий `detail`. */
export function useSceneOps(id: number) {
  const client = useQueryClient();
  const done = (got: VideoRefDetail) => put(client, got);
  return {
    add: useMutation({
      mutationFn: (b: { start_ms: number; end_ms: number }) => postJson<VideoRefDetail>(`${base}/${id}/scenes`, b),
      onSuccess: done,
    }),
    patch: useMutation({
      mutationFn: ({ sceneId, ...body }: ScenePatch & { sceneId: number }) => patchScene(id, sceneId, body),
      onSuccess: done,
    }),
    split: useMutation({
      mutationFn: ({ sceneId, at_ms }: { sceneId: number; at_ms: number }) =>
        postJson<VideoRefDetail>(`${base}/${id}/scenes/${sceneId}/split`, { at_ms }),
      onSuccess: done,
    }),
    merge: useMutation({
      mutationFn: (sceneId: number) => postJson<VideoRefDetail>(`${base}/${id}/scenes/${sceneId}/merge`, {}),
      onSuccess: done,
    }),
    drop: useMutation({
      mutationFn: (sceneId: number) => apiFetch<VideoRefDetail>(`${base}/${id}/scenes/${sceneId}`, { method: "DELETE" }),
      onSuccess: done,
    }),
  };
}

// ---------------------------------------------------------------------------
// платные: ключ до успешного ответа
// ---------------------------------------------------------------------------

/** Платная запись: ключ на набор параметров, новый — только после успеха; второй клик в полёте не шлёт POST. */
function usePaid<V, R>(
  send: (vars: V, key: string) => Promise<R>,
  onDone: (result: R) => void,
  keep: (result: R) => boolean = () => false,
) {
  const keys = useRef(new Map<string, string>());
  const inFlight = useRef(false);
  const mutation = useMutation({
    mutationFn: ({ vars, key }: { vars: V; key: string; scope: string }) => send(vars, key),
    onSuccess: (result, { scope }) => {
      if (!keep(result)) keys.current.delete(scope);
      onDone(result);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const run = (vars: V) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const scope = JSON.stringify(vars ?? null);
    let key = keys.current.get(scope);
    if (!key) {
      key = newKey();
      keys.current.set(scope, key);
    }
    mutation.mutate({ vars, key, scope });
  };
  return {
    run,
    busy: mutation.isPending,
    data: mutation.data,
    error: mutation.error ? errorText(mutation.error) : "",
    failure: mutation.error,
    reset: mutation.reset,
  };
}

/** ПЛАТНО (≈ $0.05): описание сцен композером. 409 `BUSY` — уже идёт. */
export function useDescribe(id: number) {
  const client = useQueryClient();
  return usePaid<void, DescribeResult>(
    (_v, key) => postJson<DescribeResult>(`${base}/${id}/describe`, { key }),
    (got) => put(client, got.ref),
    (got) => shouldKeepKey("describe", got),
  );
}

export type ProjectBody = {
  dress_id: number;
  library_id?: number | null;
  name?: string | null;
  /** Нет поля — умолчание сервера; `null` — без потолка. */
  cost_cap?: number | null;
};

/** ПЛАТНО (композер): проект по референсу. Ошибка композера приходит внутри 200 (`error`). */
export function useCreateProject(id: number) {
  const client = useQueryClient();
  return usePaid<ProjectBody, ProjectResult>(
    (body, key) => postJson<ProjectResult>(`${base}/${id}/project`, { ...body, key }),
    () => {
      void client.invalidateQueries({ queryKey: videoRefKeys.one(id) });
      void client.invalidateQueries({ queryKey: ["projects"] });
      void client.invalidateQueries({ queryKey: ["video-refs"] });
    },
    (got) => shouldKeepKey("project", got),
  );
}
